import React, {Children, useMemo} from 'react';

import {ElementGroup, Indent} from '@pb/kit-component-element-group';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import styles from '../../avatar-group.module.styl';
import {Size} from '../../constants';
import {AvatarGroupContext} from '../../context';
import AvatarItemComponent from '../avatar-item/avatar-item';

import type {SizesVariantsTypes} from '../../constants';
import type {AvatarItemProps} from '../avatar-item/avatar-item';
import type {ComponentProps, FC, ReactElement} from 'react';

const classes = hashedClasses(styles);

const classNames = withCssModule(styles);

type Props = {
    /**
     * Maximum total number of avatars to show before collapsing the rest into a
     * counter badge. When the number of `children` exceeds `maxCount`, the group
     * shows `maxCount - 1` avatars plus one counter item displaying the overflow
     * count. Set to `0` (default) to show all avatars without collapsing.
     */
    maxCount?: number,
    /**
     * Size token applied uniformly to all `AvatarItem` children via context.
     * Accepts any key from the `Size` object. Defaults to `Size.SMALL`.
     */
    size?: SizesVariantsTypes,
    /**
     * Props forwarded to the underlying `ElementGroup` layout wrapper. Use to
     * control spacing, direction, or other layout attributes of the group
     * container.
     */
    grouping?: ReactElement<typeof ElementGroup> | {props: ComponentProps<typeof ElementGroup>},
    /**
     * `AvatarItem` elements to display. Rendered in reverse DOM order so the
     * leftmost visible avatar appears on top when avatars overlap.
     */
    children: ReactElement<AvatarItemProps> | ReactElement<AvatarItemProps>[],
    /**
     * Additional CSS class names applied to the group container element.
     */
    className?: string,
};

const ICON_COUNT = 1;

/**
 * Renders a horizontal row of overlapping avatars with optional overflow
 * collapsing.
 *
 * When the number of `children` exceeds `maxCount`, the excess avatars are
 * hidden and a counter badge (showing the remaining count) is prepended. All
 * child `AvatarItem` components inherit the `size` prop via context, so each
 * individual item does not need to specify its own size.
 */
const AvatarGroup: FC<Props> = props => {
    const {children, size = Size.SMALL, maxCount = 0, className, grouping} = props;

    const contextValue = useMemo(() => ({size}), [size]);

    const childrenArray = Children.toArray(children);
    const isCollapsed = maxCount && maxCount < childrenArray.length;
    const avatarCount = maxCount - ICON_COUNT;
    const content = !isCollapsed ? childrenArray : childrenArray.slice(0, avatarCount);
    const groupClassName = classNames('avatar-group', className);

    return (
        <AvatarGroupContext.Provider value={contextValue}>
            <ElementGroup
                mix={groupClassName}
                indent={Indent.NONE}
                {...{
                    ...grouping?.props,
                    data: {
                        role: 'group',
                        // @ts-expect-error
                        ...grouping?.props?.data,
                    },
                }}>
                {isCollapsed ? (
                    <AvatarItemComponent
                        className={classes['avatar-group__counter']}
                        placeholder={childrenArray.length - avatarCount}
                        alt={`Hidden avatars - ${childrenArray.length - avatarCount} items`}
                    />
                ) : null}
                {content.reverse()}
            </ElementGroup>
        </AvatarGroupContext.Provider>
    );
};

AvatarGroup.displayName = 'AvatarGroup';

export default AvatarGroup;
